/**
 * TUVI - Sección "Reintegros y Descuentos" dentro del detalle de un proyecto.
 * Muestra el efecto sobre el valor total (modelo A) + el detalle, y permite
 * crear/editar/eliminar reintegros y descuentos por incumplimiento.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import AjusteCarteraModal from '../../Pagos/AjusteCarteraModal';
import '../../Pagos/AjustesCartera.css';

const formatMoney = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatDate = (f) => f ? new Date(f + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const CarteraAjustesProyecto = ({ proyectoId, proyecto, onProyectoActualizado }) => {
  const { success, error } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ show: false, editando: null });

  const info = proyecto?.proyecto || proyecto || {};
  const valorProyecto = parseFloat(info.valor_final || 0);   // ya trae los ajustes aplicados

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(
        `query($id: Int!) { carteraAjustesProyecto(proyecto_id: $id) { id proyecto_id tipo valor fecha motivo comprobante_archivo comprobante_nombre cliente_nombre usuario_registro proyecto_nombre apartamento } }`,
        { id: parseInt(proyectoId) }
      );
      setItems(d.carteraAjustesProyecto || []);
    } catch (err) { error('Error al cargar los ajustes'); }
    finally { setLoading(false); }
  }, [proyectoId, error]);

  useEffect(() => { cargar(); }, [cargar]);

  const totalReintegros = items.filter(a => a.tipo === 'reintegro').reduce((s, a) => s + Number(a.valor || 0), 0);
  const totalDescuentos = items.filter(a => a.tipo === 'descuento').reduce((s, a) => s + Number(a.valor || 0), 0);
  // El valor del proyecto YA tiene los ajustes descontados (los aplica el backend
  // al registrarlos), asi que el "original" se reconstruye sumandolos de vuelta.
  // Antes se restaban aqui otra vez y quedaba descontado dos veces.
  const valorAjustado = valorProyecto;
  const valorOriginal = valorProyecto + totalReintegros + totalDescuentos;

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este registro? Revierte su efecto en el valor y la cartera del proyecto.')) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarCarteraAjuste(id: $id) { success message } }`, { id });
      if (r.eliminarCarteraAjuste.success) { success('Eliminado'); cargar(); onProyectoActualizado && onProyectoActualizado(); }
      else error(r.eliminarCarteraAjuste.message);
    } catch (err) { error('Error al eliminar'); }
  };

  const proyFijo = {
    id: parseInt(proyectoId),
    proyecto_nombre: info.proyecto_nombre,
    apartamento: info.apartamento,
    nombre_cliente: info.nombre_cliente,
  };

  return (
    <div className="ajc-view" style={{ padding: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1e293b' }}>↩️ Reintegros y Descuentos</h3>
        <button className="ajc-btn-nuevo" onClick={() => setModal({ show: true, editando: null })}>+ Nuevo</button>
      </div>

      {/* Resumen del efecto sobre el valor */}
      <div className="ajc-resumen-valor">
        <div className="ajc-rv-item"><span>Valor original del proyecto</span><strong>{formatMoney(valorOriginal)}</strong></div>
        <div className="ajc-rv-item neg"><span>− Reintegros a cliente</span><strong>{formatMoney(totalReintegros)}</strong></div>
        <div className="ajc-rv-item neg"><span>− Descuentos por incumplimiento</span><strong>{formatMoney(totalDescuentos)}</strong></div>
        <div className="ajc-rv-item final"><span>Valor total ajustado</span><strong>{formatMoney(valorAjustado)}</strong></div>
      </div>

      {loading ? (
        <div className="ajc-empty">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="ajc-empty">Este proyecto no tiene reintegros ni descuentos registrados.</div>
      ) : (
        <div className="ajc-table-wrap">
          <table className="ajc-table">
            <thead>
              <tr><th>Tipo</th><th>Fecha</th><th style={{ textAlign: 'right' }}>Valor</th><th>Motivo</th><th style={{ textAlign: 'center' }}>Comp.</th><th></th></tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id}>
                  <td><span className={`ajc-badge ${a.tipo === 'reintegro' ? 'b-reint' : 'b-desc'}`}>{a.tipo === 'reintegro' ? 'Reintegro' : 'Descuento'}</span></td>
                  <td>{formatDate(a.fecha)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: a.tipo === 'reintegro' ? '#c2410c' : '#b45309' }}>− {formatMoney(a.valor)}</td>
                  <td className="ajc-motivo" title={a.motivo}>{a.motivo}</td>
                  <td style={{ textAlign: 'center' }}>
                    {a.comprobante_archivo
                      ? <a className="ajc-link" href={`${UPLOAD_URL}/catalogo/img/comprobantes/${a.comprobante_archivo}`} target="_blank" rel="noopener noreferrer" title="Ver comprobante">📎</a>
                      : <span className="ajc-muted">—</span>}
                  </td>
                  <td className="ajc-acciones">
                    <button className="ajc-icon" title="Editar" onClick={() => setModal({ show: true, editando: a })}>✏️</button>
                    <button className="ajc-icon danger" title="Eliminar" onClick={() => eliminar(a.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AjusteCarteraModal
        show={modal.show}
        editando={modal.editando}
        proyectoFijo={modal.editando ? null : proyFijo}
        onClose={() => setModal({ show: false, editando: null })}
        onSaved={() => { cargar(); onProyectoActualizado && onProyectoActualizado(); }}
      />
    </div>
  );
};

export default CarteraAjustesProyecto;
